import { HelaService, LogService, ModalAcceptanceContract, SheHulkService, ToastHelper } from '@hub/shared'
import ErrorMessage from '@src/enums/error-message.enum'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModal: boolean
  campaignId: string
  idNetwork: string
  opportunity: boolean
  justCloseModal: boolean
  onOpenModalRemoveInterest?: () => any
  onOpenModalContractAccepted?: () => any
  onCloseModal?: () => any
}

export default ({
  openModal,
  campaignId,
  idNetwork,
  opportunity,
  justCloseModal,
  onOpenModalRemoveInterest,
  onOpenModalContractAccepted,
  onCloseModal,
}: Props) => {
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const sheHulkService = useMemo(() => new SheHulkService(), [])
  const helaService = useMemo(() => new HelaService(), [])

  const [t] = useTranslation('modalAcceptCampaign')

  const [loading, setLoading] = useState(true)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [contract, setContract] = useState('')

  const handleCloseModal = useCallback(() => {
    if (onCloseModal && typeof onCloseModal === 'function') {
      onCloseModal()
    }
  }, [onCloseModal])

  const handleModalContractAccepted = useCallback(() => {
    if (onOpenModalContractAccepted && typeof onOpenModalContractAccepted === 'function') {
      onOpenModalContractAccepted()
    }
  }, [onOpenModalContractAccepted])

  const handleOpenModalRemoveInterest = useCallback(() => {
    if (onOpenModalRemoveInterest && typeof onOpenModalRemoveInterest === 'function') {
      onOpenModalRemoveInterest()
    }
  }, [onOpenModalRemoveInterest])

  useEffect(() => {
    if (openModal && campaignId) {
      setLoading(true)
      sheHulkService
        .getAcceptanceContract(campaignId, idNetwork)
        .then((e) => {
          if (e?.data?.message) {
            throw new Error(e?.data)
          }
          setContract(e?.contract)
        })
        .catch((err) => {
          logService.error({ message: err })
          handleCloseModal()
          const messageError = err?.data?.errorCode ? t(`errors.${(ErrorMessage as any)[err?.data?.errorCode]}`) : t('defaultError')
          toast.error(messageError, {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 10000,
            notOverClick: true,
            immediately: true,
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [openModal, campaignId, idNetwork, sheHulkService, logService, handleCloseModal, toast, t])

  const acceptContract = useCallback(() => {
    setLoadingSubmit(true)
    helaService
      .patchRecruitmentContract(campaignId, [idNetwork], 'recruited')
      .then((e) => {
        if (!!e) {
          handleCloseModal()
          handleModalContractAccepted()
        }
      })
      .catch((err) => {
        logService.error({ message: err?.data?.errorCode ?? t('errors.defaultError') })
        handleCloseModal()
        const messageError = err?.data?.errorCode ? t(`errors.${(ErrorMessage as any)[err?.data?.errorCode]}`) : t('errors.defaultError')
        toast.error(messageError, {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 10000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoadingSubmit(false)
      })
  }, [helaService, campaignId, idNetwork, handleCloseModal, handleModalContractAccepted, logService, toast, t])

  return (
    <ModalAcceptanceContract
      modalTitle={t('title')}
      openModal={openModal}
      contract={contract}
      acceptTerms={[t('readTerms'), t('breachTerms')]}
      loading={loading}
      loadingSubmit={loadingSubmit}
      contractAccepted={() => acceptContract()}
      contractRefused={() => handleOpenModalRemoveInterest()}
      closeModal={() => handleCloseModal()}
    />
  )
}
